import { useCallback, useContext } from 'react';
import useHttp from './useHttp';
import AuthContext from '../context/authContext';

const normalize = email =>
  String(email || '')
    .trim()
    .toLowerCase();

const useEmailBounceState = () => {
  const authCtx = useContext(AuthContext);
  const { isLoading, error, sendRequest } = useHttp();

  const fetchByEmails = useCallback(
    async emails => {
      const list = Array.from(
        new Set(
          (Array.isArray(emails) ? emails : [emails])
            .map(normalize)
            .filter(Boolean)
        )
      );
      if (list.length === 0) return { states: [], stateMap: new Map() };

      try {
        const responseData = await sendRequest({
          url: '/api/v1/emailBounceState/lookup',
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + authCtx.token,
          },
          body: { emails: list },
        });
        const states = Array.isArray(responseData?.states) ? responseData.states : [];
        const stateMap = new Map(states.map(s => [s.email, s]));
        return { states, stateMap };
      } catch (e) {
        throw e;
      }
    },
    [authCtx.token, sendRequest]
  );

  const fetchByEmail = useCallback(
    async email => {
      const e = normalize(email);
      if (!e) return null;
      try {
        const responseData = await sendRequest({
          url: `/api/v1/emailBounceState/${encodeURIComponent(e)}`,
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + authCtx.token,
          },
        });
        return responseData?.state || null;
      } catch (err) {
        throw err;
      }
    },
    [authCtx.token, sendRequest]
  );

  const clearBounce = useCallback(
    async (email, reason) => {
      const e = normalize(email);
      if (!e) return null;
      try {
        const responseData = await sendRequest({
          url: `/api/v1/emailBounceState/${encodeURIComponent(e)}/clear`,
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + authCtx.token,
          },
          body: { reason: reason || null },
        });
        return responseData?.state || null;
      } catch (err) {
        throw err;
      }
    },
    [authCtx.token, sendRequest]
  );

  return {
    isLoading,
    error,
    fetchByEmails,
    fetchByEmail,
    clearBounce,
  };
};

export default useEmailBounceState;
export { useEmailBounceState };
